import { useState, useCallback, useEffect } from "react";
import { Shield, RefreshCw, ChevronLeft, ChevronRight, CheckCircle, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// ── Types ────────────────────────────────────────────────────────────────────

interface SecurityEvent {
  id: string;
  organizationId: string | null;
  userId: string | null;
  eventType: string;
  severity: string;
  detail: string | null;
  ipAddress: string | null;
  timestamp: string;
  isResolved: boolean;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
}

interface ListResponse {
  items: SecurityEvent[];
  page: number;
  pageSize: number;
  totalCount: number;
}

function getToken() {
  try { return localStorage.getItem("dotnetbot.jwt") ?? ""; } catch { return ""; }
}

// ── Severity badge ────────────────────────────────────────────────────────────

const severityConfig: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; className: string }> = {
  Critical: { variant: "destructive", className: "bg-red-600 text-white border-0" },
  High:     { variant: "destructive", className: "bg-orange-500 text-white border-0" },
  Medium:   { variant: "secondary",   className: "bg-yellow-500 text-white border-0" },
  Low:      { variant: "outline",     className: "" },
};

function SeverityBadge({ severity }: { severity: string }) {
  const cfg = severityConfig[severity] ?? { variant: "outline" as const, className: "" };
  return (
    <Badge variant={cfg.variant} className={cfg.className}>
      {severity}
    </Badge>
  );
}

// ── Event type labels ─────────────────────────────────────────────────────────

const eventTypeLabels: Record<string, string> = {
  BruteForce:          "Brute Force",
  AccountLockout:      "Account Lockout",
  MfaAbuse:            "MFA Abuse",
  LockoutSpike:        "Lockout Spike",
  ExcessivePhiAccess:  "Excessive PHI Access",
  PrivilegeEscalation: "Privilege Escalation",
  BulkDataExport:      "Bulk Data Export",
};

function fmtType(t: string) { return eventTypeLabels[t] ?? t; }

// ── Detail viewer ─────────────────────────────────────────────────────────────

function DetailDialog({
  event,
  open,
  onClose,
}: {
  event: SecurityEvent | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!event) return null;
  let parsed: Record<string, unknown> | null = null;
  try { if (event.detail) parsed = JSON.parse(event.detail) as Record<string, unknown>; } catch { /* ignore */ }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" aria-hidden="true" />
            {fmtType(event.eventType)} — {event.severity}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-[120px_1fr] gap-y-1">
            <span className="text-muted-foreground font-medium">Timestamp</span>
            <span>{new Date(event.timestamp).toLocaleString()}</span>
            {event.organizationId && (
              <>
                <span className="text-muted-foreground font-medium">Org ID</span>
                <span className="font-mono text-xs">{event.organizationId}</span>
              </>
            )}
            {event.userId && (
              <>
                <span className="text-muted-foreground font-medium">User ID</span>
                <span className="font-mono text-xs">{event.userId}</span>
              </>
            )}
            {event.ipAddress && (
              <>
                <span className="text-muted-foreground font-medium">IP Address</span>
                <span className="font-mono">{event.ipAddress}</span>
              </>
            )}
            <span className="text-muted-foreground font-medium">Resolved</span>
            <span>
              {event.isResolved
                ? `Yes${event.resolvedAt ? ` (${new Date(event.resolvedAt).toLocaleString()})` : ""}`
                : "No"}
            </span>
          </div>
          {parsed && (
            <div>
              <p className="text-muted-foreground font-medium mb-1">Detail</p>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <pre tabIndex={0} role="region" aria-label="Event details" className="bg-muted rounded p-2 text-xs overflow-auto whitespace-pre-wrap">
                {JSON.stringify(parsed, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SecurityEventsPage() {
  const [events, setEvents]    = useState<SecurityEvent[]>([]);
  const [totalCount, setTotal] = useState(0);
  const [page, setPage]        = useState(1);
  const pageSize = 50;

  const [severity,  setSeverity]  = useState("");
  const [eventType, setEventType] = useState("");
  const [resolved,  setResolved]  = useState("");
  const [from,      setFrom]      = useState("");
  const [to,        setTo]        = useState("");

  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  const [detail,    setDetail]    = useState<SecurityEvent | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  const load = useCallback(async (p = page) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(p), pageSize: String(pageSize) });
      if (severity)  params.set("severity",  severity);
      if (eventType) params.set("eventType", eventType);
      if (resolved)  params.set("resolved",  resolved);
      if (from)      params.set("from",      from);
      if (to)        params.set("to",        to);

      const res = await fetch(`/api/audit/security-events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as ListResponse;
      setEvents(data.items);
      setTotal(data.totalCount);
      setPage(p);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load security events.");
    } finally { setLoading(false); }
  }, [page, severity, eventType, resolved, from, to]);

  // Reload when filters change; reset to page 1.
  useEffect(() => { void load(1); }, [severity, eventType, resolved, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 60 s.
  useEffect(() => {
    const id = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const handleResolve = async (id: string) => {
    setResolving(id);
    try {
      await fetch(`/api/audit/security-events/${id}/resolve`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: "{}",
      });
      void load(page);
    } finally { setResolving(null); }
  };

  const totalPages  = Math.max(1, Math.ceil(totalCount / pageSize));
  const openCount   = events.filter((e) => !e.isResolved).length;

  return (
    <div className="p-6 space-y-6" role="region" aria-label="Security Events">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-destructive" aria-hidden="true" />
          <h1 className="text-xl font-semibold">Security Events</h1>
          {openCount > 0 && (
            <Badge variant="destructive" className="ml-1">{openCount} open</Badge>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => void load(page)} disabled={loading}
          aria-label="Refresh security events">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={severity || "all"} onValueChange={(v) => setSeverity(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" aria-label="Filter by severity">
            <SelectValue placeholder="All severities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {["Critical", "High", "Medium", "Low"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={eventType || "all"} onValueChange={(v) => setEventType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" aria-label="Filter by event type">
            <SelectValue placeholder="All event types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All event types</SelectItem>
            {Object.entries(eventTypeLabels).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={resolved || "all"} onValueChange={(v) => setResolved(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" aria-label="Filter by resolution status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="false">Open</SelectItem>
            <SelectItem value="true">Resolved</SelectItem>
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={from}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)}
          className="w-40"
          aria-label="From date"
        />
        <Input
          type="date"
          value={to}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
          className="w-40"
          aria-label="To date"
        />
      </div>

      {/* Error */}
      {error && (
        <div role="alert" className="rounded border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Severity</TableHead>
              <TableHead>Event Type</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead>IP Address</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && events.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                  No security events found.
                </TableCell>
              </TableRow>
            )}
            {events.map((ev) => (
              <TableRow
                key={ev.id}
                className={
                  !ev.isResolved && (ev.severity === "Critical" || ev.severity === "High")
                    ? "bg-destructive/5 hover:bg-destructive/10"
                    : ""
                }
              >
                <TableCell>
                  <SeverityBadge severity={ev.severity} />
                </TableCell>
                <TableCell>
                  <button
                    className="text-left text-sm font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    onClick={() => setDetail(ev)}
                    aria-label={`View details for ${fmtType(ev.eventType)} event`}
                  >
                    {fmtType(ev.eventType)}
                  </button>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {new Date(ev.timestamp).toLocaleString()}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {ev.ipAddress ?? "—"}
                </TableCell>
                <TableCell>
                  {ev.isResolved ? (
                    <span className="flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle className="w-3.5 h-3.5" aria-hidden="true" /> Resolved
                    </span>
                  ) : (
                    <span className="text-sm text-orange-500 font-medium">Open</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {!ev.isResolved && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleResolve(ev.id)}
                      disabled={resolving === ev.id}
                      aria-label={`Resolve ${fmtType(ev.eventType)} event`}
                    >
                      {resolving === ev.id ? "Resolving…" : "Resolve"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{totalCount.toLocaleString()} total</span>
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="outline"
              disabled={page <= 1}
              onClick={() => void load(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>Page {page} of {totalPages}</span>
            <Button
              size="icon"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => void load(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <DetailDialog event={detail} open={!!detail} onClose={() => setDetail(null)} />
    </div>
  );
}
